/**
 * PATH: packages/email-hub/src/feature/lib/serverDraftStore.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-SERVERDRAFTSTORE-001
 * PURPOSE: Thin client for the Quotech-DB compose-draft API
 *          (/api/emails/compose-drafts/). This is the CROSS-DEVICE
 *          source of truth for in-progress emails — recipients, subject,
 *          the full HTML body INCLUDING inline pasted images, importance,
 *          the chosen From account, and file attachments. A draft saved
 *          on one PC resumes on another.
 * WHY:     localStorage drafts are per-device and ~5MB-capped (a pasted
 *          image silently blew the quota, losing the image + reverting
 *          text). Server storage removes the quota AND delivers
 *          "finish this draft on another PC".
 * RELATED:
 *   - draftStore.ts: localStorage mirror (instant + offline cache).
 *   - draftAttachmentStore.ts: IndexedDB mirror for attachment bytes.
 *   - useComposePage.ts / DraftsPage.tsx: consumers.
 *   - Backend: organisation/views/email_views/compose_draft_views.py
 */

import { emailApi, emailApiFetch } from './api'
import type { DraftIntent } from './draftStore'

export interface ServerDraftAttachment {
  id: string
  filename: string
  content_type: string
  size: number
  download_url: string
  created_at: string
}

export interface ServerDraft {
  id: string
  client_ref: string
  intent: DraftIntent
  intent_email_id: string
  subject: string
  body_html: string
  to: string[]
  cc: string[]
  bcc: string[]
  importance: 'low' | 'normal' | 'high'
  email_account_id: number | null
  created_at: string
  updated_at: string
  attachments: ServerDraftAttachment[]
}

export interface ServerDraftUpsertPayload {
  client_ref: string
  intent: DraftIntent
  intent_email_id?: string | null
  subject: string
  body_html: string
  to: string[]
  cc: string[]
  bcc: string[]
  importance?: 'low' | 'normal' | 'high'
  email_account_id?: number | null
}

/** List the operator's own server drafts (non-expired), freshest first. */
export async function listServerDrafts(): Promise<ServerDraft[]> {
  const res = await emailApi<{ drafts: ServerDraft[]; count: number }>(
    '/api/emails/compose-drafts/',
  )
  return res?.drafts ?? []
}

/** Create or update a draft by ``client_ref`` (idempotent upsert). */
export async function upsertServerDraft(
  payload: ServerDraftUpsertPayload,
): Promise<ServerDraft> {
  return emailApi<ServerDraft>('/api/emails/compose-drafts/', {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json' },
  })
}

/** Delete a server draft by its server id. Best-effort — a 404 is fine. */
export async function deleteServerDraft(draftId: string): Promise<void> {
  await emailApiFetch(
    `/api/emails/compose-drafts/${encodeURIComponent(draftId)}/`,
    { method: 'DELETE' },
  )
}

/** Upload one attachment to a draft (multipart). Returns its metadata. */
export async function uploadServerDraftAttachment(
  draftId: string,
  file: File,
): Promise<ServerDraftAttachment> {
  const fd = new FormData()
  fd.append('file', file, file.name)
  const res = await emailApiFetch(
    `/api/emails/compose-drafts/${encodeURIComponent(draftId)}/attachments/`,
    { method: 'POST', body: fd },
  )
  if (!res.ok) throw new Error(`Attachment upload failed (${res.status})`)
  return (await res.json()) as ServerDraftAttachment
}

/** Remove one attachment from a draft. Best-effort. */
export async function deleteServerDraftAttachment(
  draftId: string,
  attachmentId: string,
): Promise<void> {
  const d = encodeURIComponent(draftId)
  const a = encodeURIComponent(attachmentId)
  await emailApiFetch(
    `/api/emails/compose-drafts/${d}/attachments/${a}/`,
    { method: 'DELETE' },
  )
}

/**
 * Download an attachment's bytes and reconstruct a File so it flows
 * straight back into the multipart send path on resume. Returns null on
 * any failure (the chip just won't reappear; the operator can re-attach).
 */
export async function downloadServerDraftAttachment(
  att: ServerDraftAttachment,
): Promise<File | null> {
  try {
    const res = await emailApiFetch(att.download_url, { method: 'GET' })
    if (!res.ok) return null
    const blob = await res.blob()
    return new File([blob], att.filename || 'attachment', {
      type: att.content_type || blob.type || 'application/octet-stream',
    })
  } catch {
    return null
  }
}
